import React, { useEffect, useState } from 'react';
import './App.scss';
import {
  BrowserRouter,
  Switch,
  Route,
  useLocation,
  useParams,
} from "react-router-dom";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from '@date-io/date-fns';
import nl from "date-fns/locale/nl-BE";
import HomePage from "./Components/HomePage/HomePage";
import NotFound from "./Components/NotFound/NotFound";
import Header from "./Components/Common/Header/Header";
import Footer from "./Components/Common/Footer/Footer";
import Brand from "./Components/Brand/Brand";
import { ThemeProvider } from 'styled-components';
import { lightTheme, darkTheme } from './theme';
import { GlobalStyles } from './global';
import BookingSystem from "./Components/BookingSystem/BookingSystem";
import {getBrands, getFooterData, getSalesEmployeeByBrand, getSalesEmployeeBySlug} from "./Components/HTTPS/apisInterface";
import Form from "./Components/Form/Form";
import ReactPixel from 'react-facebook-pixel';
import NewUsedCar from "./Components/NewUsedCar/NewUsedCar";
import ReactGA from 'react-ga';
import { employeeMatchesSalespersonSlug, normalizeSalespersonSlug } from './utils/salespersonSlug';

const reactPixelInitOptions = {
  autoConfig: true,
  debug: false,
};

function getCurrentBrandFromPath(brands, pathname, locationSearch) {
  const pathParts = pathname.substr(1).split('/');
  const currentBrandMatch = brands.filter(item => pathParts[0] === item.permalink.toLowerCase());

  if (currentBrandMatch.length === 0) return null;

  const brand = { ...currentBrandMatch[0] };
  brand.condition = pathParts[1];
  brand.link = brand.permalink.toLowerCase();

  // Only set currentCarName when path has old /booking/carName structure (5+ segments with 'booking' at index 3)
  if (pathParts.length > 4 && pathParts[3] === 'booking') {
    brand.currentCarName = pathParts[4];
  }

  if (pathParts.length >= 3) {
    const currentExactLocation = brand.locations.filter(item => item.permalink === pathParts[2]);
    if (currentExactLocation.length === 0) return null;
    brand.currentLocation = currentExactLocation[0];
    brand.currentLocationFullName = currentExactLocation[0].name;
  }

  return brand;
}

function DirectSalespersonRoute({ brands }) {
  const { salespersonSlug, brandType } = useParams();
  const location = useLocation();
  const [isResolving, setIsResolving] = useState(true);
  const [resolvedRoute, setResolvedRoute] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const slugFromPath = normalizeSalespersonSlug(salespersonSlug || brandType);

    const resolveRoute = async () => {
      if (!slugFromPath || brands.length === 0) {
        if (isMounted) setIsResolving(false);
        return;
      }

      const normalizedSlug = slugFromPath;
      const candidateConditions = ['new', 'used', 'fleet', 'test'];

      // First resolve by canonical employee slug from WP (not dependent on today's availability).
      try {
        const employeeMeta = await getSalesEmployeeBySlug(normalizedSlug);
        if (employeeMeta) {
          const preferredConditions = employeeMeta.appointmentTypes
            .filter((condition) => candidateConditions.includes(condition));
          const conditionCandidates = preferredConditions.length > 0 ? preferredConditions : candidateConditions;
          const locationSlugs = employeeMeta.locations || [];

          for (const brand of brands) {
            if (!Array.isArray(brand.locations) || brand.locations.length === 0) continue;
            for (const condition of conditionCandidates) {
              const matchingLocation = brand.locations.find((brandLocation) => locationSlugs.includes(brandLocation.permalink));
              if (!matchingLocation) continue;
              if (isMounted) {
                setResolvedRoute({
                  brand,
                  condition,
                  currentLocation: matchingLocation,
                  slug: normalizedSlug,
                });
                setIsResolving(false);
              }
              return;
            }
          }
        }
      } catch (e) {
        // Fall back to availability-based lookup below.
      }

      const formattedDate = new Date().toISOString().split('T')[0];
      for (const brand of brands) {
        if (!Array.isArray(brand.locations) || brand.locations.length === 0) continue;

        for (const condition of candidateConditions) {
          for (const brandLocation of brand.locations) {
            try {
              const employees = await getSalesEmployeeByBrand(brandLocation.name, formattedDate, condition);
              const match = (Array.isArray(employees) ? employees : []).find((employee) =>
                employeeMatchesSalespersonSlug(employee, normalizedSlug)
              );

              if (match) {
                if (isMounted) {
                  setResolvedRoute({
                    brand,
                    condition,
                    currentLocation: brandLocation,
                    slug: normalizedSlug,
                  });
                  setIsResolving(false);
                }
                return;
              }
            } catch (e) {
              // Continue searching other brand/location/condition combinations.
            }
          }
        }
      }

      if (isMounted) {
        setResolvedRoute(null);
        setIsResolving(false);
      }
    };

    resolveRoute();

    return () => {
      isMounted = false;
    };
  }, [brands, salespersonSlug, brandType, location.search]);

  if (isResolving) return null;
  if (!resolvedRoute) return <NotFound />;

  const brandForBooking = {
    ...resolvedRoute.brand,
    condition: resolvedRoute.condition,
    link: resolvedRoute.brand.permalink.toLowerCase(),
    currentLocation: resolvedRoute.currentLocation,
    currentLocationFullName: resolvedRoute.currentLocation.name,
  };

  return (
    <BookingSystem
      reactGA={ReactGA}
      brandCondition={resolvedRoute.condition}
      location={resolvedRoute.currentLocation}
      brand={brandForBooking}
      initialSalespersonSlug={resolvedRoute.slug}
      startCalendarOnNextDay
    />
  );
}

function DirectSalespersonBookingRoute({ brands, urlSearchParam }) {
  const { salespersonSlug } = useParams();
  const location = useLocation();

  const isBrandSlug = brands.some((brand) => brand.permalink?.toLowerCase() === salespersonSlug?.toLowerCase());
  if (isBrandSlug) return <NotFound />;

  // Booking page depends on the selected timeslot state; if missing (e.g. direct refresh),
  // show NotFound for now. We can later make this state-less if needed.
  if (!location.state) return <NotFound />;

  return (
    <Form
      reactGA={ReactGA}
      urlSearchParam={urlSearchParam}
      brandCondition={location.state?.brand?.condition}
      isDirectSalespersonBooking={true}
      {...location.state}
    />
  );
}

function AppRoutes({ brands, homePageText, showHomePageText }) {
  const location = useLocation();
  const [theme, setTheme] = useState('light');
  const [currentBrand, setCurrentBrand] = useState(null);
  const [footerBrandData, setFooterBrandData] = useState(null);
  const [isHomePage, setHomePage] = useState(true);
  const [isRedirectURL, setIsRedirectUrl] = useState(null);
  const [lastProcessedPathname, setLastProcessedPathname] = useState(null);
  const footerBrandNameRef = React.useRef(null);

  // Sync all route-dependent state and analytics in useEffect (never during render)
  useEffect(() => {
    const pathname = location.pathname;
    const search = location.search;

    if (pathname === '/') {
      setHomePage(true);
      setFooterBrandData(null);
      footerBrandNameRef.current = null;
      setCurrentBrand(null);
      setTheme('light');
      ReactPixel.track("Home Page View", {});
      ReactGA.pageview("/");
      setLastProcessedPathname(pathname);
      return;
    }

    setHomePage(false);
    setTheme('light');
    setIsRedirectUrl(new URLSearchParams(search).get('car_url'));

    const brand = getCurrentBrandFromPath(brands, pathname, search);
    // Only clear footer when leaving brand pages or switching to a different brand
    if (!brand || (footerBrandNameRef.current !== null && brand.name !== footerBrandNameRef.current)) {
      setFooterBrandData(null);
      if (!brand) footerBrandNameRef.current = null;
    }
    setCurrentBrand(brand);

    if (brand) {
      if (pathname.split('/').length === 2) {
        ReactGA.pageview(`/${brand.name}`);
      } else if (pathname.split('/').length === 3) {
        ReactGA.pageview(`/${brand.name}/${brand.condition}`);
      } else if (pathname.split('/').length === 4 && !pathname.endsWith('/booking')) {
        ReactPixel.track("Car Booking Page View", {});
        ReactGA.pageview(`/${brand.name}/${brand.condition}/${brand.currentLocation?.name}`);
      } else if ((pathname.split('/').length === 4 || pathname.split('/').length === 5) && pathname.endsWith('/booking')) {
        ReactPixel.track("Form Page View", {});
        ReactGA.pageview(`/${brand.name}/${brand.condition}/${brand.currentLocation?.name}/form`);
      } else if (pathname.split('/').length === 5 && !pathname.endsWith('/booking')) {
        // Employee direct link: /brand/condition/location/salesperson-slug
        ReactPixel.track("Car Booking Page View", {});
        const slug = pathname.split('/').pop();
        ReactGA.pageview(`/${brand.name}/${brand.condition}/${brand.currentLocation?.name}/${slug}`);
      }
    }

    setLastProcessedPathname(pathname);
  }, [location.pathname, location.search, brands]);

  useEffect(() => {
    const brandName = currentBrand?.name;
    const needsFooter = brandName && (
      !footerBrandData ||
      footerBrandNameRef.current !== brandName
    );
    if (needsFooter) {
      getFooterData(brandName).then((response) => {
        setFooterBrandData(response);
        footerBrandNameRef.current = brandName;
      });
    }
  }, [currentBrand, footerBrandData]);

  if (brands.length === 0) return null;

  const pathSegments = location.pathname.split('/').filter(Boolean);
  const isLocationProcessed = lastProcessedPathname === location.pathname;

  return (
    <ThemeProvider theme={theme === 'light' ? lightTheme : darkTheme}>
      <GlobalStyles />
      {!isHomePage && <Header brand={currentBrand} />}
      <Switch location={location}>
        <Route exact path="/">
          <HomePage reactGA={ReactGA} showHomePageText={showHomePageText} homePageText={homePageText} brands={brands} />
        </Route>
        <Route exact path="/:salespersonSlug/booking">
          {!isLocationProcessed ? null : (
            <DirectSalespersonBookingRoute brands={brands} urlSearchParam={isRedirectURL} />
          )}
        </Route>
        <Route exact path="/:brandType">
          {!isLocationProcessed ? null : currentBrand === null ? (
            <DirectSalespersonRoute brands={brands} />
          ) : (
            <Brand reactGA={ReactGA} switchTheme={setTheme} brand={currentBrand} />
          )}
        </Route>
        <Route exact path="/:brandType/:brandCondition">
          {!isLocationProcessed ? null : currentBrand === null ? (
            <NotFound />
          ) : currentBrand.condition === 'new' || currentBrand.condition === 'used' ? (
            <NewUsedCar reactGA={ReactGA} brandCondition={currentBrand.condition} brand={currentBrand} />
          ) : (
            <NotFound />
          )}
        </Route>
        <Route exact path="/:brandType/:brandCondition/:brandLocation">
          {!isLocationProcessed ? null : currentBrand === null ? (
            <NotFound />
          ) : currentBrand.currentLocation ? (
            <BookingSystem reactGA={ReactGA} brandCondition={currentBrand.condition} location={currentBrand.currentLocation} brand={currentBrand} />
          ) : (
            <NotFound />
          )}
        </Route>
        <Route exact path="/:brandType/:brandCondition/:brandLocation/booking">
          {!isLocationProcessed ? null : currentBrand === null || location.state === undefined ? (
            <NotFound />
          ) : (
            <Form reactGA={ReactGA} urlSearchParam={isRedirectURL} brandCondition={currentBrand.condition} {...location.state} />
          )}
        </Route>
        <Route exact path="/:brandType/:brandCondition/:brandLocation/:salespersonSlug">
          {!isLocationProcessed ? null : currentBrand === null ? (
            <NotFound />
          ) : currentBrand.currentLocation ? (
            <BookingSystem reactGA={ReactGA} brandCondition={currentBrand.condition} location={currentBrand.currentLocation} brand={currentBrand} initialSalespersonSlug={normalizeSalespersonSlug(pathSegments[pathSegments.length - 1])} startCalendarOnNextDay />
          ) : (
            <NotFound />
          )}
        </Route>
        <Route exact path="/:brandType/:brandCondition/:brandLocation/:salespersonSlug/booking">
          {!isLocationProcessed ? null : currentBrand === null || location.state === undefined ? (
            <NotFound />
          ) : (
            <Form reactGA={ReactGA} urlSearchParam={isRedirectURL} brandCondition={currentBrand.condition} {...location.state} />
          )}
        </Route>
        <Route path="*" component={NotFound} />
      </Switch>
      {footerBrandData && <Footer socialMedia={currentBrand} brand={footerBrandData} theme={theme} />}
    </ThemeProvider>
  );
}

function App() {
  const [brands, setBrands] = useState([]);
  const [homePageText, setHomePageText] = useState(null);
  const [showHomePageText, setShowHomePageText] = useState(null);

  useEffect(() => {
    getBrands().then(res => {
      ReactPixel.init('226381818882123', { em: "christina@metropool.be" }, reactPixelInitOptions);
      ReactGA.initialize('UA-150377924-2');
      setBrands(res.brands);
      setHomePageText(res.home_page_text);
      setShowHomePageText(res.show_text);
    });
  }, []);

  return (
    <React.Fragment>
      {brands.length > 0 ? (
        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={nl}>
          <BrowserRouter>
            <AppRoutes brands={brands} homePageText={homePageText} showHomePageText={showHomePageText} />
          </BrowserRouter>
        </MuiPickersUtilsProvider>
      ) : ''}
    </React.Fragment>
  );
}

export default App;
